"use client";

// Third-party Imports
import { createSlice, createAsyncThunk, current } from "@reduxjs/toolkit";
import { toast } from "react-toastify";

// Type Imports
import type { DevelopmentTypes } from "@/types/apps/DevelopmentTypes";

// Server Imports
import {
  doGetDocuments,
  doGetTemplates,
  doGetTemplateById,
  doGetReviews,
  doGetFeedbacks,
  doGetSprints,
  doGetReviewsManage,
} from "@/app/server/actions";

const initialState: DevelopmentTypes = {
  documents: [],
  filteredDocuments: [],
  documentsLoading: "idle" as string | "pending" as string | "succeeded" as string | "failed" as string,
  templates: [],
  filteredTemplates: [],
  templatesLoading: "idle" as string | "pending" as string | "succeeded" as string | "failed" as string,
  loading: "idle" as string | "pending" as string | "succeeded" as string | "failed" as string,
  error: null,
  total: 0,
  lastErrorToastTime: 0,
  selectedTemplate: {
    data: {},
    columns: [],
    loading: "idle" as string | "pending" as string | "succeeded" as string | "failed" as string,
    error: null,
    hasUnsavedChanges: false,
    isSaving: false,
  },
  reviews: {
    reviews: [],
    filteredReviews: [],
    loading: "idle" as string | "pending" as string | "succeeded" as string | "failed" as string,
    error: null,
    selectedTypes: ["user", "assessor"],
    lastErrorToastTime: 0,
  },
  feedbacks: {
    feedbacks: [],
    filteredFeedbacks: [],
    loading: "idle" as string | "pending" as string | "succeeded" as string | "failed" as string,
    error: null,
  },
  sprints: {
    sprints: [],
    filteredSprints: [],
    loading: "idle" as string | "pending" as string | "succeeded" as string | "failed" as string,
    error: null,
    lastErrorToastTime: 0,
  },
} as any;

export const getDocuments = createAsyncThunk<any, any, any>("development/fetch_documents", async (_: void, { rejectWithValue }) => {
  try {
    const response = await doGetDocuments();

    if (response === null || response === undefined) {
      return rejectWithValue("Failed to load documents");
    }

    const documents = Array.isArray(response) ? response : [];


    return {
      data: documents,
    };
  } catch (error) {
    return rejectWithValue("Failed to load documents");
  }
});

export const getTemplates = createAsyncThunk<any, any, any>("development/fetch_templates", async (_: void, { rejectWithValue }) => {
  try {
    const response = await doGetTemplates();

    if (response === null || response === undefined) {
      return rejectWithValue("Failed to load templates");
    }

    const templates = Array.isArray(response) ? response : [];


    return {
      data: templates,
    };
  } catch (error) {
    return rejectWithValue("Failed to load templates");
  }
});

export const getTemplate = createAsyncThunk<any, any, any>("development/fetch_template", async (id: string, { rejectWithValue }) => {
  try {
    const response = await doGetTemplateById(id);

    return {
      data: response,
    };
  } catch (err) {
    return rejectWithValue(err);
  }
});

export const getReviews = createAsyncThunk<any, any, any>("development/fetch_reviews", async (_: void, { rejectWithValue }) => {
  try {
    const response = await doGetReviews();

    if (response === null || response === undefined) {
      return rejectWithValue("Failed to load reviews");
    }

    const reviews = Array.isArray(response) ? response : [];


    return {
      data: reviews,
    };
  } catch (error) {
    return rejectWithValue("Failed to load reviews");
  }
});

export const getFeedbacks = createAsyncThunk<any, any, any>("development/fetch_feedbacks", async (_: void, { rejectWithValue }) => {
  try {
    const response = await doGetFeedbacks();

    if (response === null || response === undefined) {
      return rejectWithValue("Failed to load feedbacks");
    }

    const feedbacks = Array.isArray(response) ? response : [];


    return {
      data: feedbacks,
    };
  } catch (error) {
    return rejectWithValue("Failed to load feedbacks");
  }
});

export const getSprints = createAsyncThunk<any, any, any>("development/fetch_sprints", async (_: void, { rejectWithValue }) => {
  try {
    const response = await doGetSprints();

    if (response === null || response === undefined) {
      return rejectWithValue("Failed to load sprints");
    }

    const sprints = Array.isArray(response) ? response : [];


    return {
      data: sprints,
    };
  } catch (error) {
    return rejectWithValue("Failed to load sprints");
  }
});

export const getManageReviews = createAsyncThunk<any, any, any>("development/fetch_manage_reviews", async (_: void, { rejectWithValue }) => {
  try {
    const response = await doGetReviewsManage();

    if (response === null || response === undefined) {
      return rejectWithValue("Failed to load reviews");
    }

    const reviews = Array.isArray(response) ? response : [];


    return {
      data: reviews,
    };
  } catch (error) {
    return rejectWithValue("Failed to load reviews");
  }
});

// Save template
export const saveTemplate = createAsyncThunk<any, { id: string; columns: any[] }, { rejectValue: string }>(
  "development/save_template",
  async ({ id, columns }, { rejectWithValue }) => {
    try {
      const data = columns.map((item, index) => {
        // Preserve _id for existing items (from database)
        // New items won't have _id and will be created
        const payload: any = {
          ...item,
          position: index,
        };

        // Keep the _id if it exists (for updates)
        // Remove the frontend-only 'id' field as backend uses position
        delete payload.id;

        // Ensure data field is a string or undefined (backend expects String or Null)
        // Convert non-string data to JSON string
        if (payload.data !== undefined && typeof payload.data !== 'string') {
          try {
            payload.data = JSON.stringify(payload.data);
          } catch {
            payload.data = undefined;
          }
        }

        return payload;
      });

      const response = await fetch(`${process.env.API_URL}/modules/development/templates/${id}`, {
        method: "PATCH",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });

      if ([401, 403].includes(response.status)) {
        return rejectWithValue("Unauthorized");
      }

      if (!response.ok) {
        // Try to get error message from response
        try {
          const errorData = await response.json();


          return rejectWithValue(errorData?.message || "Failed to save template");
        } catch {
          return rejectWithValue("Failed to save template");
        }
      }

      return { success: true };
    } catch (error: any) {
      return rejectWithValue(error?.message || "Failed to save template");
    }
  }
);

const filterEventsUsingCheckbox = (reviews: any[], selectedTypes: any[]) => {
  if (selectedTypes.length === 0) return [];
  if (!selectedTypes.includes("user") && !selectedTypes.includes("assessor")) return reviews;
  if (selectedTypes.includes("user") && selectedTypes.includes("assessor")) return reviews;
  if (!selectedTypes.includes("user") && selectedTypes.includes("assessor")) return reviews.filter(review => review.type === "assessor");
  if (selectedTypes.includes("user") && !selectedTypes.includes("assessor")) return reviews.filter(review => review.type === "user");
};

export const developmentSlice = createSlice({
  name: "development",
  initialState: initialState,
  reducers: {
    filterDocuments: (state, action) => {
      const { search } = action.payload;

      const currentState = current(state);
      let data = currentState.filteredDocuments;

      if (search && search.length > 0) {
        data = data.filter((document: any) => document.name.toLowerCase().includes(search.toLowerCase()));
      }

      state.documents = data;
    },
    filterTemplates: (state, action) => {
      const { search } = action.payload;

      const currentState = current(state);
      let data = currentState.filteredTemplates;

      if (search && search.length > 0) {
        data = data.filter((template: any) => template.name.toLowerCase().includes(search.toLowerCase()));
      }

      state.templates = data;
    },
    updateColumns: (state, action) => {
      // Create a map of existing items by their identifier to preserve all data
      const existingItemsMap = new Map();

      state.selectedTemplate.columns.forEach((item: any) => {
        const itemIdentifier = item._id?.$oid || item.id;

        if (itemIdentifier) {
          existingItemsMap.set(itemIdentifier, item);
        }
      });

      // Update columns while preserving all existing data
      state.selectedTemplate.columns = action.payload.map((item: any) => {
        const itemIdentifier = item._id?.$oid || item.id;
        const existingItem = existingItemsMap.get(itemIdentifier);

        // If we have an existing item, merge with it to preserve all fields (especially data)
        if (existingItem) {
          // Preserve the data field from existing item if the new item doesn't have it or it's different
          const preservedData = existingItem.data;
          const mergedItem = { ...existingItem, ...item };

          // Ensure data field is preserved from existing item
          if (preservedData !== undefined) {
            mergedItem.data = preservedData;
          }

          // Ensure data is a string if it exists
          if (mergedItem.data && typeof mergedItem.data !== 'string') {
            mergedItem.data = JSON.stringify(mergedItem.data);
          }

          return mergedItem;
        }

        // For new items without existing data, ensure data is a string if present
        if (item.data && typeof item.data !== 'string') {
          return { ...item, data: JSON.stringify(item.data) };
        }

        return item;
      });

      state.selectedTemplate.hasUnsavedChanges = true;
    },
    deleteItem: (state, action) => {
      const { itemId } = action.payload;

      state.selectedTemplate.columns = state.selectedTemplate.columns.filter((item: any) => {
        // Match by _id for existing items, or by id for new items
        const itemIdentifier = item._id?.$oid || item.id;


        return itemIdentifier !== itemId;
      });
      state.selectedTemplate.hasUnsavedChanges = true;
    },
    addItem: (state, action) => {
      const newItem = {
        ...action.payload,
        id: Date.now(),
        position: state.selectedTemplate.columns.length,
        value: "",
        title: "",
        subtitle: "",
        description: "",
        data: undefined, // Ensure data field is properly initialized
        assessor: false,
        required: false,
      };

      state.selectedTemplate.columns.push(newItem);
      state.selectedTemplate.hasUnsavedChanges = true;
    },
    updateColumnField: (state, action) => {
      const { id, field, value } = action.payload;


      // Find by _id for existing items, or by id for new items
      const column = state.selectedTemplate.columns.find((col: any) => {
        const itemIdentifier = col._id?.$oid || col.id;


        return itemIdentifier === id;
      });

      if (column) {
        (column as any)[field] = value;
        state.selectedTemplate.hasUnsavedChanges = true;
      }
    },
    toggleAssessor: (state, action) => {
      // Find by _id for existing items, or by id for new items
      const column = state.selectedTemplate.columns.find((col: any) => {
        const itemIdentifier = col._id?.$oid || col.id;


        return itemIdentifier === action.payload.id;
      });

      if (column) {
        (column as any).assessor = !(column as any).assessor;
        state.selectedTemplate.hasUnsavedChanges = true;
      }
    },
    toggleRequired: (state, action) => {
      // Find by _id for existing items, or by id for new items
      const column = state.selectedTemplate.columns.find((col: any) => {
        const itemIdentifier = col._id?.$oid || col.id;


        return itemIdentifier === action.payload.id;
      });

      if (column) {
        (column as any).required = !(column as any).required;
        state.selectedTemplate.hasUnsavedChanges = true;
      }
    },
    updateColumnData: (state, action) => {
      const { id, data } = action.payload;

      // Find by _id for existing items, or by id for new items
      const columnIndex = state.selectedTemplate.columns.findIndex((col: any) => {
        const itemIdentifier = col._id?.$oid || col.id;


        return itemIdentifier === id;
      });

      if (columnIndex !== -1) {
        // Directly mutate the column (Immer will handle immutability)
        // This avoids creating a new array reference which would re-initialize drag-and-drop
        const column = state.selectedTemplate.columns[columnIndex];

        (column as any).data = data;
        state.selectedTemplate.hasUnsavedChanges = true;
      }
    },
    resetUnsavedChanges: state => {
      state.selectedTemplate.hasUnsavedChanges = false;
    },
    clearSelectedTemplate: state => {
      state.selectedTemplate.data = {};
      state.selectedTemplate.columns = [];
      state.selectedTemplate.loading = "idle";
      state.selectedTemplate.error = null;
      state.selectedTemplate.hasUnsavedChanges = false;
      state.selectedTemplate.isSaving = false;
    },
    updateTemplateInfo: (state, action) => {
      const { name, description } = action.payload;

      if (state.selectedTemplate.data && state.selectedTemplate.data.data) {
        state.selectedTemplate.data = {
          ...state.selectedTemplate.data,
          data: {
            ...state.selectedTemplate.data.data,
            name,
            description,
          },
        };
      }
    },
    filterReviewLabel(state, action) {
      const { key, search } = action.payload;
      const index = state.reviews.selectedTypes.indexOf(key);

      if (index !== -1) {
        state.reviews.selectedTypes.splice(index, 1);
      } else {
        state.reviews.selectedTypes.push(key);
      }

      const currentState = current(state);
      let data: any = currentState.reviews.filteredReviews;

      data = filterEventsUsingCheckbox(data, currentState.reviews.selectedTypes);

      if (search && search.length > 0) {
        data = data.filter((review: any) => {
          const reviewName = review.review.type + " " + review.review.name.toLowerCase();
          const teamName = review.review?.team?.name ?? "";

          return reviewName.toLowerCase().includes(search.toLowerCase()) || teamName.toLowerCase().includes(search.toLowerCase());
        });
      }

      state.reviews.reviews = data;
    },
    filterAllLabels: (state: any, action) => {
      const { checked, search } = action.payload;

      state.reviews.selectedTypes = checked ? ["user", "assessor"] : [];
      const currentState = current(state);

      let data: any = currentState.reviews.filteredReviews;

      data = filterEventsUsingCheckbox(data, currentState.reviews.selectedTypes);

      if (search && search.length > 0) {
        data = data.filter((review: any) => {
          const reviewName = review.review.type + " " + review.review.name.toLowerCase();
          const teamName = review.review?.team?.name ?? "";

          return reviewName.toLowerCase().includes(search.toLowerCase()) || teamName.toLowerCase().includes(search.toLowerCase());
        });
      }

      state.reviews.reviews = data;
    },
    filterBytext: (state: any, action) => {
      const { search } = action.payload;
      const currentState = current(state);
      let data: any = currentState.reviews.filteredReviews;

      if (search && search.length > 0) {
        data = data.filter((review: any) => {
          const reviewName = review.review.type + " " + review.review.name.toLowerCase();
          let user: any = {};

          try { user = JSON.parse(review.review?.user?.details ?? "{}"); } catch { user = {}; }
          let assessor: any = {};

          try { assessor = review?.review?.assessor ? JSON.parse(review.review.assessor.details) : {}; } catch { assessor = {}; }
          const teamName = review.review?.team?.name ?? "";

          return (
            reviewName.toLowerCase().includes(search.toLowerCase()) ||
            teamName.toLowerCase().includes(search.toLowerCase()) ||
            (user.fullname ?? "").toLowerCase().includes(search.toLowerCase()) ||
            (assessor.fullname ?? "").toLowerCase().includes(search.toLowerCase())
          );
        });
      }

      data = filterEventsUsingCheckbox(data, currentState.reviews.selectedTypes);

      state.reviews.reviews = data;
    },
    filterFeedbacksBytext: (state: any, action) => {
      const { search } = action.payload;
      const currentState = current(state);
      let data: any = currentState.feedbacks.filteredFeedbacks;

      if (search && search.length > 0) {
        data = data.filter((feedback: any) => {
          let user: any = {};

          try {
            user = JSON.parse(feedback.review.user.details);
          } catch {
            user = {};
          }

          const nameMatch = feedback.review?.name?.toLowerCase().includes(search.toLowerCase()) ?? false;
          const fullnameMatch = user.fullname?.toLowerCase().includes(search.toLowerCase()) ?? false;


          return nameMatch || fullnameMatch;
        });
      }

      state.feedbacks.feedbacks = data;
    },
    filterSprints: (state, action) => {
      const { search } = action.payload;

      const currentState = current(state);
      let data = currentState.sprints.filteredSprints;

      if (search && search.length > 0) {
        data = data.filter((sprint: any) => sprint.name.toLowerCase().includes(search.toLowerCase()));
      }

      state.sprints.sprints = data;
    },
    filterManageReviews: (state, action) => {
      const { search } = action.payload;

      const currentState = current(state);
      let data = currentState.reviews.filteredReviews;

      if (search && search.length > 0) {
        data = data.filter((template: any) => template.name.toLowerCase().includes(search.toLowerCase()));
      }

      state.reviews.reviews = data;
    },
  },
  extraReducers: builder => {
    // Documents
    builder.addCase(getDocuments.pending, state => {
      state.documentsLoading = "pending";
    });
    builder.addCase(getDocuments.rejected, (state, action) => {
      // Don't show error if the request was aborted
      if (action.meta.aborted) {
        state.documentsLoading = "idle";

        return;
      }

      state.documentsLoading = "failed";
      state.error = action.payload;

      // Only show toast if there's an actual error and enough time has passed since last toast
      // This prevents duplicates in React Strict Mode (500ms debounce)
      const now = Date.now();
      const timeSinceLastToast = now - ((state as any).lastErrorToastTime || 0);

      if (action.payload && timeSinceLastToast > 500) {
        (state as any).lastErrorToastTime = now;
        toast.error("Failed to load documents. Please try again.", {
          toastId: "documents-load-error",
        });
      }
    });
    builder.addCase(getDocuments.fulfilled, (state, action) => {
      state.documents = action.payload.data;
      state.filteredDocuments = action.payload.data;
      state.total = action.payload.data.length;
      state.documentsLoading = "succeeded";
    });

    // Templates
    builder.addCase(getTemplates.pending, state => {
      state.templatesLoading = "pending";
    });
    builder.addCase(getTemplates.rejected, (state, action) => {
      if (action.meta.aborted) {
        state.templatesLoading = "idle";

        return;
      }

      state.templatesLoading = "failed";
      state.error = action.payload;

      const now = Date.now();
      const timeSinceLastToast = now - ((state as any).lastErrorToastTime || 0);

      if (action.payload && timeSinceLastToast > 500) {
        (state as any).lastErrorToastTime = now;
        toast.error("Failed to load templates. Please try again.", {
          toastId: "templates-load-error",
        });
      }
    });
    builder.addCase(getTemplates.fulfilled, (state, action) => {
      state.templates = action.payload.data;
      state.filteredTemplates = action.payload.data;
      state.total = action.payload.data.length;
      state.templatesLoading = "succeeded";
    });

    // Template by id
    builder.addCase(getTemplate.pending, state => {
      state.selectedTemplate.loading = "pending";
      state.selectedTemplate.error = null;
    });
    builder.addCase(getTemplate.rejected, (state, action) => {
      if (action.meta.aborted) {
        // Aborted requests are not errors — keep loading as pending so UI doesn't flash error
        return;
      }

      state.selectedTemplate.loading = "failed";
      state.selectedTemplate.error = action.payload;

      if (action.payload) {
        toast.error("Failed to load template. Please try again.", {
          toastId: "template-load-error",
        });
      }
    });
    builder.addCase(getTemplate.fulfilled, (state, action) => {
      state.selectedTemplate.columns = action.payload.data.columns || [];
      state.selectedTemplate.data = action.payload.data;
      state.selectedTemplate.loading = "succeeded";
      state.selectedTemplate.error = null;
      state.selectedTemplate.hasUnsavedChanges = false;
    });

    // User reviews
    builder.addCase(getReviews.pending, state => {
      state.reviews.loading = "pending";
    });
    builder.addCase(getReviews.rejected, (state, action) => {
      // Don't show error if the request was aborted (React Strict Mode or component unmount)
      if (action.meta.aborted) {
        state.reviews.loading = 'idle';

        return;
      }

      state.reviews.loading = "failed";
      state.reviews.error = action.payload;

      // Only show toast if there's an actual error (not just undefined from abort)
      if (action.payload) {
        toast.error("Failed to load reviews. Please try again.", {
          toastId: "reviews-load-error",
        });
      }
    });
    builder.addCase(getReviews.fulfilled, (state, action) => {
      state.reviews.reviews = action.payload.data;
      state.reviews.filteredReviews = action.payload.data;
      state.reviews.loading = "succeeded";
    });

    // User feedbacks
    builder.addCase(getFeedbacks.pending, state => {
      state.feedbacks.loading = "pending";
    });
    builder.addCase(getFeedbacks.rejected, (state, action) => {
      // Don't show error if the request was aborted (React Strict Mode or component unmount)
      if (action.meta.aborted) {
        state.feedbacks.loading = "idle";
        return;
      }

      state.feedbacks.loading = "failed";
      state.feedbacks.error = action.payload;

      // Only show toast if there's an actual error (not just undefined from abort)
      if (action.payload) {
        toast.error("Failed to load feedbacks. Please try again.", {
          toastId: "feedbacks-load-error",
        });
      }
    });
    builder.addCase(getFeedbacks.fulfilled, (state, action) => {
      state.feedbacks.feedbacks = action.payload.data;
      state.feedbacks.filteredFeedbacks = action.payload.data;
      state.feedbacks.loading = "succeeded";
    });

    // manage sprints
    builder.addCase(getSprints.pending, state => {
      state.sprints.loading = "pending";
    });
    builder.addCase(getSprints.rejected, (state, action) => {
      if (action.meta.aborted) {
        state.sprints.loading = "idle";

        return;
      }

      state.sprints.loading = "failed";
      state.sprints.error = action.payload;

      const now = Date.now();
      const timeSinceLastToast = now - ((state.sprints as any).lastErrorToastTime || 0);

      if (action.payload && timeSinceLastToast > 500) {
        (state.sprints as any).lastErrorToastTime = now;
        toast.error("Failed to load sprints. Please try again.", {
          toastId: "sprints-load-error",
        });
      }
    });
    builder.addCase(getSprints.fulfilled, (state, action) => {
      state.sprints.sprints = action.payload.data;
      state.sprints.filteredSprints = action.payload.data;
      state.sprints.loading = "succeeded";
    });

    // Manage entity reviews
    builder.addCase(getManageReviews.pending, state => {
      state.reviews.loading = "pending";
    });
    builder.addCase(getManageReviews.rejected, (state, action) => {
      if (action.meta.aborted) {
        state.reviews.loading = 'idle';

        return;
      }

      state.reviews.loading = "failed";
      state.reviews.error = action.payload;

      const now = Date.now();
      const timeSinceLastToast = now - ((state.reviews as any).lastErrorToastTime || 0);

      if (action.payload && timeSinceLastToast > 500) {
        (state.reviews as any).lastErrorToastTime = now;
        toast.error("Failed to load reviews. Please try again.", {
          toastId: "manage-reviews-load-error",
        });
      }
    });
    builder.addCase(getManageReviews.fulfilled, (state, action) => {
      state.reviews.reviews = action.payload.data;
      state.reviews.filteredReviews = action.payload.data;
      state.reviews.loading = "succeeded";
    });

    // Save template
    builder.addCase(saveTemplate.pending, state => {
      state.selectedTemplate.isSaving = true;
    });
    builder.addCase(saveTemplate.rejected, (state, action) => {
      state.selectedTemplate.isSaving = false;

      if (action.payload !== "Unauthorized") {
        toast.error(action.payload || "Failed to save template", {
          toastId: "template-save-error",
        });
      }
    });
    builder.addCase(saveTemplate.fulfilled, (state, action) => {
      state.selectedTemplate.isSaving = false;
      state.selectedTemplate.hasUnsavedChanges = false;

      // Update positions after save (preserve _id and other fields)
      if (action.meta.arg.columns) {
        state.selectedTemplate.columns = action.meta.arg.columns.map((item, index) => ({
          ...item,
          position: index,
        }));
      }

      toast.success("Template saved successfully", {
        toastId: "template-save-success",
      });
    });
  },
});

export const {
  filterDocuments,
  filterTemplates,
  updateColumns,
  deleteItem,
  addItem,
  updateColumnField,
  toggleAssessor,
  toggleRequired,
  updateColumnData,
  resetUnsavedChanges,
  clearSelectedTemplate,
  updateTemplateInfo,
  filterReviewLabel,
  filterAllLabels,
  filterBytext,
  filterFeedbacksBytext,
  filterSprints,
  filterManageReviews,
} = developmentSlice.actions;

export default developmentSlice.reducer;
